/**
 * platformApi — a SEPARATE axios instance for the platform (system) admin
 * console. Mirrors lib/apiV2.ts (Bearer token attach, envelope unwrap,
 * single-flight refresh-and-retry on 401) but points at `/platform/*` and
 * carries the platform token from platformAuthStore — never the org token.
 *
 * ASSUMPTION (flag for backend confirmation): platform responses are wrapped
 * in the same {success, data, error} envelope as every other /api/v1 route.
 * The LLD's example bodies show the unwrapped shape; this client assumes the
 * envelope wraps them, matching every other Go handler in this codebase.
 */
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlatformApiError,
  type OrgSummary,
  type PlatformLoginResult,
  type ProvisionOrgInput,
  type ProvisionOrgResult,
} from './types';
import { PLATFORM_STORAGE_KEYS, usePlatformAuthStore } from './platformAuthStore';

const BASE_URL =
  (import.meta.env.VITE_GO_API_BASE_URL as string | undefined) ??
  'http://localhost:4000/api/v1';

export const platformApi = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ---- Request interceptor: attach the PLATFORM bearer token ------------------

platformApi.interceptors.request.use((config) => {
  const token = usePlatformAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---- Single-flight refresh ---------------------------------------------------

let refreshPromise: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
  const refreshToken = localStorage.getItem(PLATFORM_STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) throw new Error('No platform refresh token available');

  const resp = await axios.post<{ success: boolean; data: PlatformLoginResult }>(
    `${BASE_URL}/platform/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );

  const result = resp.data.data;
  if (!result?.tokens?.access_token) throw new Error('Platform refresh returned no tokens');

  usePlatformAuthStore.getState().updateTokens({
    accessToken: result.tokens.access_token,
    refreshToken: result.tokens.refresh_token,
  });

  return result.tokens.access_token;
}

// ---- Response interceptor: unwrap envelope, handle 401 -----------------------

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

platformApi.interceptors.response.use(
  (response) => {
    if (response.status === 204 || response.data === '' || response.data == null) {
      return undefined as unknown as typeof response;
    }
    const envelope = response.data as { success: boolean; data?: unknown; message?: string };
    if (!envelope.success) {
      throw new PlatformApiError('REQUEST_FAILED', envelope.message ?? 'Request failed');
    }
    return envelope.data as unknown as typeof response;
  },

  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig | undefined;
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/') ?? false;

    if (error.response?.status === 401 && originalRequest?._retry) {
      usePlatformAuthStore.getState().logout();
      return Promise.reject(
        new PlatformApiError('SESSION_EXPIRED', 'Session expired, please log in again', 401),
      );
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      const refreshToken = localStorage.getItem(PLATFORM_STORAGE_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        usePlatformAuthStore.getState().logout();
        return Promise.reject(new PlatformApiError('UNAUTHENTICATED', 'Session expired', 401));
      }

      originalRequest._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = doRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return platformApi(originalRequest);
      } catch {
        usePlatformAuthStore.getState().logout();
        return Promise.reject(
          new PlatformApiError('SESSION_EXPIRED', 'Session expired, please log in again', 401),
        );
      }
    }

    const body = error.response?.data as
      | { success: false; error?: { code: string; message: string } }
      | undefined;
    if (body?.error) {
      return Promise.reject(
        new PlatformApiError(body.error.code, body.error.message, error.response?.status ?? 0),
      );
    }
    return Promise.reject(error);
  },
);

// ---- Auth-specific helpers ----------------------------------------------------

export const platformAuthApi = {
  login: (username: string, password: string, device_id?: string) =>
    platformApi
      .post('/platform/auth/login', { username, password, ...(device_id ? { device_id } : {}) })
      .then((r) => r as unknown as PlatformLoginResult),

  refresh: (refresh_token: string) =>
    platformApi
      .post('/platform/auth/refresh', { refresh_token })
      .then((r) => r as unknown as PlatformLoginResult),

  logout: (refresh_token: string) =>
    platformApi
      .post('/platform/auth/logout', { refresh_token })
      .then(() => undefined),

  me: () =>
    platformApi.get('/platform/me').then((r) => r as unknown as { id: string; full_name: string }),
};

// ---- Query keys ----------------------------------------------------------

export const platformKeys = {
  orgs: () => ['platform', 'orgs'] as const,
};

// ---- Orgs: list + provision ------------------------------------------------

export function usePlatformOrgs() {
  return useQuery<OrgSummary[]>({
    queryKey: platformKeys.orgs(),
    queryFn: () => platformApi.get('/platform/orgs').then((r) => r as unknown as OrgSummary[]),
    staleTime: 1000 * 30,
  });
}

export function useProvisionOrg() {
  const queryClient = useQueryClient();
  return useMutation<ProvisionOrgResult, unknown, ProvisionOrgInput>({
    // The backend expects a FLAT body (verified live), not the nested
    // {org, admin} form model. Flatten at the network boundary — keep the
    // nested ProvisionOrgInput as the internal form shape. Optional org
    // fields are only sent when non-empty (backend defaults tz→Asia/Kolkata,
    // currency→INR).
    mutationFn: (input) => {
      const body = {
        code: input.org.code,
        name: input.org.name,
        ...(input.org.timezone ? { timezone: input.org.timezone } : {}),
        ...(input.org.currency ? { currency: input.org.currency } : {}),
        admin_full_name: input.admin.full_name,
        admin_phone: input.admin.phone,
        admin_username: input.admin.username,
        admin_password: input.admin.password,
      };
      return platformApi
        .post('/platform/orgs', body)
        .then((r) => r as unknown as ProvisionOrgResult);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformKeys.orgs() });
    },
  });
}
